import { useState } from "react";
import axios from "axios";
import { fetchPlaylistDownloadUrlApi } from "@src/apis/playlistApis";
import { fetchTrackDownloadUrlApi } from "@src/apis/trackApis";
import { useNotification } from "@src/context/NotificationContext";
import { useManagerEntitlement } from "@src/queries/managerConfigurationQueries";
import { MANAGER_ENTITLEMENTS } from "@src/types/managerConfiguration";
import { useAuthUser, useRole } from "./useAuth";

export const usePublicS3Downloader = (): {
  downloadFile: (fileUrl: string, fileName?: string) => Promise<void>;
  downloadTrackById: (trackId: string) => Promise<void>;
  downloadPlaylistById: (playlistId: string) => Promise<void>;
  /**
   * `false` for a manager without the `audio-downloads` entitlement. The
   * download buttons stay visible either way; trying one is what tells them.
   */
  canDownloadAudio: boolean;
  isDownloading: boolean;
  error: string | null;
} => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const role = useRole();
  const user = useAuthUser();

  const { addNotification } = useNotification();
  const canDownloadAudio = useManagerEntitlement(
    MANAGER_ENTITLEMENTS.AUDIO_DOWNLOADS
  );

  /**
   * A view-only manager has no downloads at all. Notified here rather than
   * through `error`, so every click says so — not only the first one.
   */
  const checkDownloadAllowed = (): boolean => {
    if (canDownloadAudio) return true;
    addNotification(
      "You don't have permission to download audio. Ask an admin to enable downloads for your account.",
      "error"
    );
    return false;
  };

  /** Managers keep a daily track allowance; admins download without limit. */
  const checkDownloadLimit = (): boolean => {
    if (!checkDownloadAllowed()) return false;
    if (role === "manager") {
      if (user?.id) {
        const today = new Date().toISOString().split("T")[0];
        const key = `daily_track_downloads_${today}_${user.id}`;
        const count = parseInt(localStorage.getItem(key) || "0", 10);
        if (count >= 5) {
          setError("You have reached your daily download limit of 5 tracks.");
          return false;
        }
        localStorage.setItem(key, (count + 1).toString());
      }
    }
    return true;
  };

  const downloadFile = async (
    fileUrl: string,
    fileName?: string
  ): Promise<void> => {
    setIsDownloading(true);
    setError(null);

    try {
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || fileUrl.split("/").pop() || "download";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  const clickDownloadLink = (href: string, fileName: string): void => {
    const link = document.createElement("a");
    link.href = href;
    link.download = fileName;
    link.click();
  };

  /**
   * The backend hands out a short-lived presigned S3 link (it checks who may
   * download, ROVR-4349), and the browser downloads from S3 directly, so a
   * whole mix never sits in memory.
   *
   * A Strapi without ROVR-4349 has no `download-url` route and answers 405.
   * There the old route is still public, so fall back to it. That lets this
   * dashboard ship before or after that Strapi deploy without breaking
   * downloads. Remove the fallback once production Strapi has ROVR-4349: the
   * old route then needs a token, which a plain link can't send.
   */
  const downloadFromSignedUrl = async (
    fetchSignedUrl: () => Promise<{ data: { url: string } }>,
    legacyPath: string,
    fileName: string,
    failureMessage: string
  ): Promise<void> => {
    setIsDownloading(true);
    setError(null);

    try {
      const { data } = await fetchSignedUrl();
      clickDownloadLink(data.url, fileName);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 405) {
        clickDownloadLink(
          `${process.env.REACT_APP_API_URL}${legacyPath}`,
          fileName
        );
      } else {
        setError(
          status === 403
            ? "You don't have permission to download this audio."
            : failureMessage
        );
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadTrackById = async (trackId: string): Promise<void> => {
    if (!checkDownloadLimit()) return;
    await downloadFromSignedUrl(
      () => fetchTrackDownloadUrlApi(trackId),
      `/api/tracks/${trackId}/download`,
      `track-${trackId}`,
      "Failed to download track"
    );
  };

  const downloadPlaylistById = async (playlistId: string): Promise<void> => {
    if (!checkDownloadAllowed()) return;
    await downloadFromSignedUrl(
      () => fetchPlaylistDownloadUrlApi(playlistId),
      `/api/playlists/${playlistId}/download`,
      `playlist-${playlistId}`,
      "Failed to download playlist"
    );
  };

  return {
    downloadTrackById,
    downloadPlaylistById,
    downloadFile,
    canDownloadAudio,
    isDownloading,
    error,
  };
};
